import { useState, useRef } from "react";
import { FolderOpen, Upload, Download, Eye, Trash2, Pencil, FileText, Image as ImageIcon, FileSpreadsheet, File, AlertTriangle, Cloud, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { formatDateTime } from "../../lib/formatters";
import { useJobDocuments, formatFileSize } from "../../hooks/useJobDocuments";

const iconFor = (doc) => {
  const ct = doc.content_type || "";
  if (ct.startsWith("image/")) return <ImageIcon className="w-4 h-4 text-purple-600" />;
  if (ct.includes("spreadsheet") || ct.includes("excel") || ct === "text/csv") return <FileSpreadsheet className="w-4 h-4 text-green-600" />;
  if (ct === "application/pdf" || ct.includes("word")) return <FileText className="w-4 h-4 text-red-600" />;
  return <File className="w-4 h-4 text-slate-500" />;
};

const DocumentsTab = ({ jobId, canManageJobs }) => {
  const { documents, categories, loading, uploading, uploadDocuments, updateDocument, deleteDocument, openDocument } = useJobDocuments(jobId);
  const [filter, setFilter] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const fileRef = useRef(null);

  const allCategories = Array.from(new Set([...categories, ...documents.map((d) => d.category)])).filter(Boolean);
  const visible = filter === "all" ? documents : documents.filter((d) => d.category === filter);
  const countFor = (c) => documents.filter((d) => d.category === c).length;

  const openUpload = () => {
    setUploadCategory(filter !== "all" ? filter : (categories[0] || ""));
    setUploadDescription("");
    setUploadFiles([]);
    setUploadOpen(true);
  };

  const submitUpload = async () => {
    if (!uploadFiles.length || !uploadCategory) return;
    const ok = await uploadDocuments(uploadFiles, uploadCategory, uploadDescription);
    if (ok) setUploadOpen(false);
  };

  return (
    <Card data-testid="documents-tab-card">
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5" />
          Documents ({documents.length})
        </CardTitle>
        {canManageJobs && (
          <Button onClick={openUpload} disabled={uploading} data-testid="upload-document-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload Documents
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="document-category-filters">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${filter === "all" ? "bg-slate-800 text-white border-slate-800" : "bg-white hover:bg-slate-50"}`}
            data-testid="doc-filter-all"
          >
            All ({documents.length})
          </button>
          {allCategories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${filter === c ? "bg-slate-800 text-white border-slate-800" : "bg-white hover:bg-slate-50"}`}
              data-testid={`doc-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c} ({countFor(c)})
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-muted-foreground text-center py-8">Loading documents...</p>
        ) : visible.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground" data-testid="documents-empty">
            <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>No documents {filter !== "all" ? `in ${filter}` : "yet"}.</p>
            {canManageJobs && <p className="text-sm">Upload contracts, plans, submittals and more — they're saved to OneDrive under this job.</p>}
          </div>
        ) : (
          <div className="divide-y rounded-lg border" data-testid="documents-list">
            {visible.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 p-3 hover:bg-slate-50" data-testid={`document-row-${doc.id}`}>
                <div className="shrink-0">{iconFor(doc)}</div>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => openDocument(doc)}
                    className="font-medium text-left hover:underline truncate block max-w-full"
                    title={doc.original_name}
                    data-testid={`document-name-${doc.id}`}
                  >
                    {doc.original_name}
                  </button>
                  <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="inline-flex px-1.5 py-0.5 rounded bg-slate-100 text-slate-700" data-testid={`document-category-${doc.id}`}>{doc.category}</span>
                    <span>{formatFileSize(doc.size)}</span>
                    <span>· {doc.uploaded_by} · {formatDateTime(doc.uploaded_at)}</span>
                    {doc.storage === "onedrive" && <span className="inline-flex items-center gap-1 text-sky-700"><Cloud className="w-3 h-3" /> OneDrive</span>}
                    {doc.storage_warning && (
                      <span className="inline-flex items-center gap-1 text-amber-700" title="OneDrive was unreachable; file is kept in the database">
                        <AlertTriangle className="w-3 h-3" /> Not in OneDrive
                      </span>
                    )}
                  </div>
                  {doc.description && <p className="text-sm text-slate-600 mt-0.5 truncate">{doc.description}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" title="View" onClick={() => openDocument(doc)} data-testid={`document-view-${doc.id}`}><Eye className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" title="Download" onClick={() => openDocument(doc, true)} data-testid={`document-download-${doc.id}`}><Download className="w-4 h-4" /></Button>
                  {canManageJobs && (
                    <>
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => setEditing({ ...doc })} data-testid={`document-edit-${doc.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" title="Delete" className="text-red-600 hover:text-red-700" onClick={() => setDeleting(doc)} data-testid={`document-delete-${doc.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg" data-testid="upload-document-dialog">
          <DialogHeader><DialogTitle>Upload Documents</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger data-testid="upload-document-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {allCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Files *</Label>
              <Input
                ref={fileRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.tif,.tiff,.dwg,.dxf,.zip,.msg,.eml"
                onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
                data-testid="upload-document-files"
              />
              {uploadFiles.length > 0 && (
                <p className="text-xs text-muted-foreground" data-testid="upload-document-selected">
                  {uploadFiles.length} file{uploadFiles.length === 1 ? "" : "s"} selected · {formatFileSize(uploadFiles.reduce((s, f) => s + f.size, 0))}
                </p>
              )}
              <p className="text-xs text-muted-foreground">PDF, Word, Excel, images, CAD, ZIP · max 25 MB each</p>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea rows={2} value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} placeholder="e.g., Signed contract rev. 2" data-testid="upload-document-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button onClick={submitUpload} disabled={uploading || !uploadFiles.length || !uploadCategory} data-testid="upload-document-submit">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="edit-document-dialog">
          <DialogHeader><DialogTitle>Edit Document</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <p className="text-sm font-medium truncate">{editing.original_name}</p>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="edit-document-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="edit-document-description" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={async () => { await updateDocument(editing.id, { category: editing.category, description: editing.description || "" }); setEditing(null); }} data-testid="edit-document-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-document-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.original_name}" will be removed from this job{deleting?.storage === "onedrive" ? " and from OneDrive" : ""}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={async () => { await deleteDocument(deleting.id); setDeleting(null); }} data-testid="delete-document-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default DocumentsTab;
